import React from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "./theme";
import { Icon } from "./Icon";

export function View({ style, row, children, ...rest }) {
  return (
    <div style={{ display: "flex", flexDirection: row ? "row" : "column", boxSizing: "border-box", ...style }} {...rest}>
      {children}
    </div>
  );
}

export function Text({ style, children, numberOfLines, ...rest }) {
  const clamp = numberOfLines
    ? { display: "-webkit-box", WebkitLineClamp: numberOfLines, WebkitBoxOrient: "vertical", overflow: "hidden" }
    : {};
  return (
    <span style={{ color: colors.onSurface, fontWeight: fonts.regular, ...clamp, ...style }} {...rest}>
      {children}
    </span>
  );
}

export function Press({ style, onClick, disabled, children, testID, ...rest }) {
  return (
    <div
      data-testid={testID}
      role="button"
      onClick={disabled ? undefined : onClick}
      style={{ cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, userSelect: "none", ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Card({ children, style }) {
  return (
    <View style={{
      backgroundColor: colors.surface, borderRadius: 20, border: `1px solid ${colors.border}`,
      padding: 16, boxShadow: "0 6px 16px rgba(15,37,64,0.06)", ...style,
    }}>
      {children}
    </View>
  );
}

export function Button({ title, onPress, variant = "primary", icon, iconRight, loading, disabled, style, testID, small }) {
  const bg = { primary: colors.brandPrimary, secondary: colors.surfaceSecondary, ghost: "transparent", danger: colors.error }[variant];
  const fg = { primary: colors.onBrandPrimary, secondary: colors.onSurfaceSecondary, ghost: colors.brandPrimary, danger: colors.onError }[variant];
  const border = variant === "secondary" ? colors.border : "transparent";
  return (
    <Press
      testID={testID}
      onClick={disabled || loading ? undefined : onPress}
      disabled={disabled || loading}
      style={{
        backgroundColor: bg, border: `${variant === "secondary" ? 1 : 0}px solid ${border}`,
        height: small ? 44 : 54, borderRadius: 16, display: "flex", flexDirection: "row",
        alignItems: "center", justifyContent: "center", padding: "0 20px", gap: 8, width: "100%",
        transition: "opacity 0.15s ease", ...style,
      }}
    >
      {loading ? (
        <span style={{ color: fg, fontWeight: fonts.semibold }}>…</span>
      ) : (
        <>
          {icon && <Icon name={icon} size={20} color={fg} weight="bold" />}
          <span style={{ fontWeight: fonts.semibold, fontSize: small ? 14 : 16, color: fg }}>{title}</span>
          {iconRight && <Icon name={iconRight} size={20} color={fg} weight="bold" />}
        </>
      )}
    </Press>
  );
}

export function Chip({ label, selected, onPress, testID }) {
  return (
    <Press testID={testID} onClick={onPress} style={{
      height: 36, padding: "0 16px", borderRadius: 999, flexShrink: 0, display: "flex",
      alignItems: "center", justifyContent: "center",
      backgroundColor: selected ? colors.brandPrimary : colors.surfaceSecondary,
      border: `1px solid ${selected ? colors.brandPrimary : colors.border}`,
    }}>
      <span style={{ fontWeight: fonts.medium, fontSize: 14, color: selected ? colors.onBrandPrimary : colors.onSurfaceSecondary }}>{label}</span>
    </Press>
  );
}

export function ProgressBar({ progress, color, height = 8 }) {
  return (
    <div style={{ height, backgroundColor: colors.ringTrack, borderRadius: 999, overflow: "hidden", width: "100%" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, progress))}%`, height, backgroundColor: color ?? colors.brandPrimary, borderRadius: 999, transition: "width 0.3s ease" }} />
    </div>
  );
}

export function IconTile({ icon, tint, accent, size = 52 }) {
  return (
    <View style={{ width: size, height: size, borderRadius: 14, backgroundColor: tint, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon name={icon} size={size * 0.5} color={accent} weight="fill" />
    </View>
  );
}

export function ScreenHeader({ title, subtitle, onBack, right, big }) {
  const navigate = useNavigate();
  const hasBack = onBack !== undefined;
  return (
    <View style={{
      paddingTop: 20, paddingBottom: 12, padding: "20px 20px 12px",
      backgroundColor: colors.surface, borderBottom: hasBack ? `1px solid ${colors.divider}` : "none",
      position: "sticky", top: 0, zIndex: 20,
    }}>
      <View row style={{ alignItems: "center", minHeight: 40 }}>
        {hasBack && (
          <Press testID="header-back" onClick={onBack ?? (() => navigate(-1))} style={{
            marginRight: 8, width: 40, height: 40, borderRadius: 20, display: "flex",
            alignItems: "center", justifyContent: "center", backgroundColor: colors.surfaceSecondary, flexShrink: 0,
          }}>
            <Icon name="arrow-left" size={20} color={colors.onSurface} weight="bold" />
          </Press>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={1} style={{ fontWeight: fonts.bold, fontSize: big ? 28 : 20, color: colors.onSurface }}>{title}</Text>
          {subtitle && <Text numberOfLines={1} style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted, marginTop: 2 }}>{subtitle}</Text>}
        </View>
        {right}
      </View>
    </View>
  );
}

export function Segmented({ items, value, onChange }) {
  return (
    <View row style={{ backgroundColor: colors.surfaceSecondary, borderRadius: 12, padding: 4 }}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <Press key={it.key} testID={`seg-${it.key}`} onClick={() => onChange(it.key)} style={{
            flex: 1, height: 38, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: active ? colors.surface : "transparent", boxShadow: active ? "0 2px 6px rgba(15,37,64,0.08)" : "none",
          }}>
            <span style={{ fontWeight: active ? fonts.semibold : fonts.medium, fontSize: 13, color: active ? colors.onSurface : colors.muted }}>{it.label}</span>
          </Press>
        );
      })}
    </View>
  );
}

export function EmptyState({ icon = "confetti", title, sub }) {
  return (
    <View style={{ alignItems: "center", justifyContent: "center", padding: 40, gap: 12 }}>
      <View style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: colors.brandTertiary, alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={34} color={colors.brandPrimary} weight="duotone" />
      </View>
      <Text style={{ fontWeight: fonts.semibold, fontSize: 17, color: colors.onSurface, textAlign: "center" }}>{title}</Text>
      {sub && <Text style={{ fontWeight: fonts.regular, fontSize: 14, color: colors.muted, textAlign: "center" }}>{sub}</Text>}
    </View>
  );
}

export function Loading() {
  return (
    <View style={{ flex: 1, minHeight: "100vh", alignItems: "center", justifyContent: "center", backgroundColor: colors.surface }}>
      <div className="melly-spinner" />
    </View>
  );
}

export function SectionTitle({ children, style }) {
  return (
    <View style={{ marginBottom: 12, ...style }}>
      <span style={{ fontWeight: fonts.bold, fontSize: 13, letterSpacing: 0.6, color: colors.muted, textTransform: "uppercase" }}>{children}</span>
    </View>
  );
}
